import { useEffect, useMemo, useState } from 'react';

import { isAborted, isNetworkFailure, isServerFailure } from '@art-suite/art-core-ts';
import { useQueryCacheMutation } from '@kaiizen/react-persistent-cache';
import { ErrorWithStatus, getErrorCommunicationStatus } from '@kaiizen/zod-less-api';

import { useAuth } from './useAuth';

/** How long the code-entry screen waits before offering a resend, so a code still on its way can arrive. */
export const LOGIN_CODE_RESEND_REVEAL_MS = 5_000;
/** How long after each resend before another is offered: long enough for a slow SMS to arrive first. */
export const LOGIN_CODE_RESEND_COOLDOWN_MS = 15_000;

export type LoginDeliveryMethod = 'email' | 'sms';

type LoginStage = 'request' | 'verification';

/** A failed login request, ready to show: the message, how alarming it is, and which step and channel failed. */
export type LoginError = {
  message: string;
  severity: 'error' | 'warning';
  stage: LoginStage;
  deliveryMethod: LoginDeliveryMethod;
};

type SentCode = { contact: string; sentAt: number; resent: boolean };

type LoginFailure = { error: unknown; stage: LoginStage; contact: string };

const getDeliveryMethod = (contact: string): LoginDeliveryMethod => contact.includes('@') ? 'email' : 'sms';

const isValidEmail = (email: string) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email) && !email.split('@')[1].includes('..');

/** 10 digits, or 11 starting with the country code 1, in any formatting. */
const isValidUsPhoneNumber = (phoneNumber: string) => {
  const digits = phoneNumber.replace(/\D/g, '');
  return digits.length === 10 || (digits.length === 11 && digits.startsWith('1'));
};

const isValidContact = (contact: string) => getDeliveryMethod(contact) === 'email' ? isValidEmail(contact) : isValidUsPhoneNumber(contact);

/** The code as sent: its digits, so a code pasted as "123 456" still works. */
const getCodeDigits = (authCode: string) => authCode.replace(/\D/g, '');

const getLoginErrorMessage = (error: unknown) =>
  error instanceof ErrorWithStatus
    ? error.remoteMessage ?? error.message
    : error instanceof Error
    ? error.message
    : String(error);

const presentLoginError = ({ error, stage, contact }: LoginFailure): LoginError | undefined => {
  const communicationStatus = getErrorCommunicationStatus(error);
  if (isAborted(communicationStatus)) return undefined;
  const deliveryMethod = getDeliveryMethod(contact);
  if (isNetworkFailure(communicationStatus)) {
    return {
      message: 'Could not connect to our services. Please check your internet connection and try again.',
      severity: 'warning',
      stage,
      deliveryMethod,
    };
  }
  const message = isServerFailure(communicationStatus)
    ? "We're sorry, we had an internal server failure. Please try again later."
    : getLoginErrorMessage(error);
  return { message, severity: 'error', stage, deliveryMethod };
};

const getResendAt = ({ sentAt, resent }: SentCode) => sentAt + (resent ? LOGIN_CODE_RESEND_COOLDOWN_MS : LOGIN_CODE_RESEND_REVEAL_MS);

/**
 * Milliseconds until the code last sent may be resent: LOGIN_CODE_RESEND_REVEAL_MS after a first
 * send, LOGIN_CODE_RESEND_COOLDOWN_MS after a resend. Re-renders on each whole second of the wait,
 * so a countdown stays current.
 */
const useMillisecondsUntilResend = (sentCode: SentCode | undefined) => {
  const [now, setNow] = useState(() => Date.now());
  const resendAt = sentCode && getResendAt(sentCode);
  const millisecondsLeft = sentCode && resendAt ? Math.max(0, resendAt - Math.max(now, sentCode.sentAt)) : 0;
  useEffect(() => {
    if (!resendAt || millisecondsLeft === 0) return;
    const remaining = Math.max(0, resendAt - Date.now());
    const timer = setTimeout(() => setNow(Date.now()), remaining % 1000 || Math.min(remaining, 1000));
    return () => clearTimeout(timer);
  }, [resendAt, millisecondsLeft]);
  return millisecondsLeft;
};

/**
 * Login by one-time code, shared by every login screen: the user enters an email or phone
 * number, we send a code there, and they enter the code to sign in.
 *
 * - Every action (request, resend, verify, change contact) clears the previous error first.
 * - `canRequestCode` needs an email or a US phone number and `canVerifyCode` a six-digit code, and
 *   neither, nor `canResendCode`, allows a request while another is in flight (`requestPending`).
 * - The code entry step offers a resend LOGIN_CODE_RESEND_REVEAL_MS after the code goes out, and
 *   each resend clears the typed code and holds the next one back for LOGIN_CODE_RESEND_COOLDOWN_MS,
 *   counting down in `secondsUntilResend`. The server keeps every unexpired code valid, so a
 *   late-arriving earlier code still works after a resend.
 * - Verifying never retries: the server spends a code on first receipt, so a retry after a lost
 *   response re-posts a spent code and earns a 401, which signs the user back out.
 * - `codeContact` is where the code went. It survives the 401 a wrong code earns (which clears the
 *   stored credentials), and is restored from the credentials after an app restart mid-login.
 */
export const useLoginWithCode = () => {
  const { credentials, credentialsMutators } = useAuth();
  const [contact, setContact] = useState('');
  const [authCode, setAuthCode] = useState('');
  const [sentCode, setSentCode] = useState<SentCode>();
  const [failure, setFailure] = useState<LoginFailure>();

  const codeContact = sentCode?.contact
    ?? (credentials.waitingForAuthCompletion ? credentials.email ?? credentials.phoneNumber : undefined);

  const sendCode = useQueryCacheMutation(
    async (codeDestination: string, resent: boolean) => {
      await (getDeliveryMethod(codeDestination) === 'email'
        ? credentialsMutators.initiateSendEmailAuth(codeDestination)
        : credentialsMutators.initiateSendSmsAuth(codeDestination, 'sms'));
      setSentCode({ contact: codeDestination, sentAt: Date.now(), resent });
    },
    { onError: (error: unknown) => setFailure({ error, stage: 'request', contact: codeContact ?? contact.trim() }) },
  );

  const verifyCode = useQueryCacheMutation(
    (codeDestination: string, code: string) =>
      getDeliveryMethod(codeDestination) === 'email'
        ? credentialsMutators.validateSendEmailAuth(codeDestination, code)
        : credentialsMutators.validateSendSmsAuth(codeDestination, code, 'sms'),
    {
      onError: (error: unknown) => setFailure({ error, stage: 'verification', contact: codeContact ?? '' }),
      shouldRetry: () => false,
    },
  );

  const millisecondsUntilResend = useMillisecondsUntilResend(sentCode);
  const loginError = useMemo(() => failure && presentLoginError(failure), [failure]);
  const requestPending = sendCode.isPending || verifyCode.isPending;

  return {
    contact,
    setContact,
    authCode,
    setAuthCode,
    codeContact,
    codeDeliveryMethod: codeContact === undefined ? undefined : getDeliveryMethod(codeContact),
    sendingCode: sendCode.isPending,
    verifyingCode: verifyCode.isPending,
    requestPending,
    canRequestCode: !requestPending && isValidContact(contact.trim()),
    canVerifyCode: codeContact !== undefined && !requestPending && getCodeDigits(authCode).length === 6,
    /** The code entry step is offering a resend: from LOGIN_CODE_RESEND_REVEAL_MS after the first send on. */
    resendOffered: codeContact !== undefined && (!!sentCode?.resent || millisecondsUntilResend === 0),
    canResendCode: codeContact !== undefined && millisecondsUntilResend === 0 && !requestPending,
    secondsUntilResend: Math.ceil(millisecondsUntilResend / 1000),
    /** When the latest resend went out, for its confirmation; undefined until a resend succeeds. */
    codeResentAt: sentCode?.resent ? sentCode.sentAt : undefined,
    loginError,
    dismissLoginError: () => setFailure(undefined),
    requestCode: () => {
      setFailure(undefined);
      void sendCode.mutate(contact.trim(), false);
    },
    resendCode: () => {
      if (codeContact === undefined) return;
      setFailure(undefined);
      setAuthCode('');
      void sendCode.mutate(codeContact, true);
    },
    verifyCode: () => {
      if (codeContact === undefined) return;
      setFailure(undefined);
      void verifyCode.mutate(codeContact, getCodeDigits(authCode));
    },
    changeContact: () => {
      setFailure(undefined);
      setSentCode(undefined);
      setAuthCode('');
      sendCode.cancel();
      verifyCode.cancel();
      void credentialsMutators.clearToken();
    },
  };
};
